// @ts-nocheck
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Info, X as XIcon } from 'lucide-react';
import { api } from '../../utils/api.js';

/* ── Constants ──────────────────────────────────────────────────────────── */
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB — must match server multer limit
const SUPPORTED_EXT = /\.(jpe?g|jfif|png|gif|webp|avif|heic|heif|tiff?|bmp|svg)$/i;

/** Translate raw server/network errors into plain-language merchant copy */
function friendlyError(raw: string): string {
  const s = (raw || '').toLowerCase();
  if (s.includes('file too large') || s.includes('too large') || s.includes('payload too large') || s.includes('exceeds') && s.includes('mb'))
    return 'This image is too large (over 20 MB). Try saving it as a JPEG first to reduce the file size.';
  if (s.includes('not a valid image') || s.includes('not appear to be') || s.includes('unsupported'))
    return 'This file isn\'t a supported image format. Try saving it as a JPG or PNG.';
  if (s.includes('mp limit') || s.includes('megapixel') || s.includes('dimensions'))
    return 'This image is too large to process (over 25 megapixels). Try resizing it before uploading.';
  if (s.includes('network') || s.includes('failed to fetch') || s.includes('load failed') || s.includes('err_internet'))
    return 'Upload interrupted — check your internet connection and try again.';
  if (s.includes('duplicate') || s.includes('already'))
    return 'This image has already been added to this product.';
  if (s.includes('could not read') || s.includes('corrupt') || s.includes('damaged'))
    return "This file appears to be damaged and can't be opened. Try re-saving it from the original source.";
  if (s.includes('session expired') || s.includes('expired') || s.includes('401'))
    return 'Your session has expired. Please refresh the page and try again.';
  if (s.includes('server') || s.includes('500') || s.includes('internal'))
    return 'Something went wrong on our end. Please try again in a moment.';
  return raw; // fallback — pass through if no pattern matched
}

/** Validate a file client-side before upload */
function validateFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return `Too large (${sizeMB} MB) — max 20 MB`;
  }
  if (!file.type.startsWith('image/') && !SUPPORTED_EXT.test(file.name)) {
    return `Unsupported format`;
  }
  return null;
}

/* ── CSS injected once — exactly the mockup's stylesheet ──────────────────── */
const CSS = `
.iim-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.45);
  backdrop-filter: blur(3px); display: flex; align-items: center;
  justify-content: center; padding: 24px; z-index: 1000;
}
.iim-modal {
  background: var(--color-surface); border-radius: var(--r-lg);
  border: 1px solid var(--color-border); box-shadow: 0 8px 32px rgba(0,0,0,0.10);
  width: 100%; max-width: 540px; overflow: hidden;
}
.iim-modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px 0;
}
.iim-modal-title { font-size: 16px; font-weight: 600; color: var(--color-text); }
.iim-modal-subtitle { font-size: 13px; font-weight: 400; color: var(--color-text-2); margin-top: 4px; }
.iim-modal-header { align-items: flex-start; }
.iim-close-btn {
  width: 28px; height: 28px; border-radius: var(--r-sm);
  border: 1px solid var(--color-border); background: var(--color-surface-2);
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: var(--color-text-2); font-size: 14px; line-height: 1;
  transition: background 0.15s; font-family: inherit;
}
.iim-close-btn:hover { background: var(--color-border); }
.iim-tab-content { padding: 20px 24px 24px; }
.iim-dropzone {
  border: 1.5px dashed var(--color-border-strong, #CBD5E1);
  border-radius: var(--r-lg); padding: 40px 24px; text-align: center;
  background: var(--color-surface-2); cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.iim-dropzone:hover {
  border-color: var(--color-primary); background: var(--color-primary-light);
}
.iim-dropzone.drag-over {
  border-color: var(--color-primary); background: var(--color-primary-light);
}
.iim-upload-icon {
  width: 40px; height: 40px; margin: 0 auto 12px;
  background: var(--color-surface); border: 1px solid var(--color-border);
  border-radius: var(--r-md); display: flex; align-items: center; justify-content: center;
}
.iim-dz-title { font-size: 14px; font-weight: 500; color: var(--color-text); margin-bottom: 4px; }
.iim-dz-sub { font-size: 12px; color: var(--color-text-2); line-height: 1.5; }
.iim-dz-link {
  color: var(--color-primary); text-decoration: none;
  background: none; border: none; padding: 0; cursor: pointer;
  font-size: 12px; font-family: inherit;
}
.iim-dz-link:hover { text-decoration: underline; }
.iim-hint {
  font-size: 12px; color: var(--color-text-2); margin-top: 10px;
  padding: 8px 12px; background: var(--color-surface-2);
  border-radius: var(--r-sm); line-height: 1.5;
}
.iim-files-list {
  margin-top: 14px; border: 1px solid var(--color-border);
  border-radius: var(--r-md); overflow: hidden;
}
.iim-file-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 12px; border-bottom: 1px solid var(--color-border);
}
.iim-file-row:last-child { border-bottom: none; }
.iim-file-row-inner {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 12px; gap: 8px;
}
.iim-file-name {
  font-size: 12px; color: var(--color-text); flex: 1; min-width: 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.iim-match-badge {
  font-size: 11px; font-weight: 600; padding: 2px 8px;
  border-radius: 999px; white-space: nowrap; margin-left: 8px;
}
.iim-match-green { background: var(--color-green-bg); color: var(--color-green-text); }
.iim-match-yellow {
  background: var(--color-yellow-bg); color: var(--color-yellow-text);
  border: none; cursor: pointer; font-family: inherit;
  transition: opacity 0.15s;
}
.iim-match-yellow:hover { opacity: 0.8; }
.iim-assign-panel { padding: 0 12px 10px; background: var(--color-surface-2); }
.iim-assign-input {
  width: 100%; padding: 6px 10px; font-size: 12px; box-sizing: border-box;
  border: 1px solid var(--color-border); border-radius: var(--r-sm);
  background: var(--color-surface); color: var(--color-text);
  outline: none; font-family: inherit;
}
.iim-assign-input:focus { border-color: var(--color-primary); }
.iim-assign-list {
  background: var(--color-surface); border: 1px solid var(--color-border);
  border-radius: var(--r-sm); margin-top: 4px; max-height: 150px;
  overflow-y: auto; box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}
.iim-assign-item {
  padding: 7px 10px; font-size: 12px; cursor: pointer; color: var(--color-text);
}
.iim-assign-item:hover { background: var(--color-surface-2); }
.iim-modal-footer {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 24px; border-top: 1px solid var(--color-border);
}
.iim-footer-meta { font-size: 12px; color: var(--color-text-2); }
.iim-footer-actions { display: flex; gap: 8px; }
.iim-btn-ghost {
  padding: 8px 14px; border-radius: var(--r-md);
  border: 1px solid var(--color-border); background: transparent;
  font-size: 13px; color: var(--color-text-2); cursor: pointer;
  font-family: inherit; transition: background 0.15s;
}
.iim-btn-ghost:hover { background: var(--color-surface-2); }
.iim-btn-primary {
  padding: 8px 16px; border-radius: var(--r-md); border: none;
  background: var(--color-primary); color: #fff; font-size: 13px;
  font-weight: 600; cursor: pointer; font-family: inherit; transition: background 0.15s;
}
.iim-btn-primary:hover { background: var(--color-primary-hover, #1E3DB5); }
.iim-btn-primary:disabled {
  background: var(--color-text-3); color: #fff; cursor: default; opacity: 0.5;
}
.iim-btn-primary:disabled:hover { background: var(--color-text-3); }
.iim-progress-bar-track {
  height: 4px; background: var(--color-border); border-radius: 4px; overflow: hidden;
  margin-top: 10px;
}
.iim-progress-bar-fill {
  height: 100%; background: var(--color-primary); border-radius: 4px;
  transition: width 0.2s ease;
}
.iim-file-thumb {
  width: 28px; height: 28px; border-radius: 4px; object-fit: cover;
  background: var(--color-surface-2); border: 1px solid var(--color-border);
  flex-shrink: 0;
}
.iim-status-icon {
  width: 18px; height: 18px; flex-shrink: 0; display: flex;
  align-items: center; justify-content: center;
}
.iim-status-icon svg { width: 14px; height: 14px; }
@keyframes iimSpin {
  to { transform: rotate(360deg); }
}
.iim-spinner {
  width: 14px; height: 14px; animation: iimSpin 0.7s linear infinite;
}
.iim-file-error {
  font-size: 11px; color: var(--color-red, #dc2626); padding: 0 12px 6px;
  line-height: 1.3;
}
.iim-retry-btn {
  font-size: 11px; color: var(--color-primary); background: none;
  border: none; cursor: pointer; font-family: inherit; padding: 2px 0;
  text-decoration: underline; text-underline-offset: 2px;
}
.iim-retry-btn:hover { opacity: 0.8; }
`;

let cssInjected = false;
function injectCSS() {
  if (cssInjected) return;
  cssInjected = true;
  const el = document.createElement('style');
  el.textContent = CSS;
  document.head.appendChild(el);
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */
function normaliseForMatch(str) {
  return (str || '').toLowerCase().replace(/[/\\:*?"<>|]/g, '').trim();
}

function slugify(str) {
  return (str || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function extractPositionSuffix(stem) {
  const match = stem.match(/^(.+)_(\d+)$/);
  if (match) return { base: match[1], position: parseInt(match[2], 10) };
  return { base: stem, position: null };
}

function matchImageToProduct(filename, products) {
  const ext = filename.split('.').pop().toLowerCase();
  const stem = filename.slice(0, filename.length - ext.length - 1).toLowerCase();
  const { base, position } = extractPositionSuffix(stem);

  const bySkuExact = {};
  const bySkuPartial = [];
  const byTitleSlug = {};

  for (const p of products) {
    for (const v of p.variants || []) {
      const sku = normaliseForMatch(v.sku);
      if (sku.length >= 2) {
        if (!bySkuExact[sku]) bySkuExact[sku] = { product: p, variant: v };
        bySkuPartial.push({ sku, product: p, variant: v });
      }
    }
    const slug = slugify(p.title);
    if (slug && !byTitleSlug[slug]) byTitleSlug[slug] = p;
  }

  if (bySkuExact[base]) return { product: bySkuExact[base].product, variant: bySkuExact[base].variant, position };

  for (const { sku, product, variant } of bySkuPartial) {
    if (sku.length >= 3 && base.includes(sku)) return { product, variant, position };
  }

  if (byTitleSlug[base]) return { product: byTitleSlug[base], variant: null, position };

  for (const [slug, product] of Object.entries(byTitleSlug)) {
    if (slug.length >= 3 && base.includes(slug)) return { product, variant: null, position };
  }

  return { product: null, variant: null, position: null };
}

async function uploadFile(file, productId, variantId) {
  return api.uploadImage(file, productId, variantId);
}

/* ── Component ───────────────────────────────────────────────────────────── */
export default function ImageUploadModal({ catalogId, products, onClose, onImagesUpdated }) {
  injectCSS();

  const [matches, setMatches]               = useState([]);
  const [dragOver, setDragOver]             = useState(false);
  const [openAssignIdx, setOpenAssignIdx]   = useState(null);
  const [searchQueries, setSearchQueries]   = useState({});
  const [manualAssignments, setManualAssignments] = useState({});
  const [mode, setMode]                     = useState('idle');
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [fileStatuses, setFileStatuses]     = useState({});
  const [successCount, setSuccessCount]     = useState(0);

  const fileInputRef   = useRef(null);
  const folderInputRef = useRef(null);
  const assignRefs     = useRef({});

  // Data URLs from FileReader don't need cleanup (no revokeObjectURL needed)

  useEffect(() => {
    if (openAssignIdx === null) return;
    function handleClickOutside(e) {
      const el = assignRefs.current[openAssignIdx];
      if (el && !el.contains(e.target)) setOpenAssignIdx(null);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openAssignIdx]);

  // Read file bytes as a data URL with correct MIME — works reliably on all OSes
  function readThumb(file: File): Promise<string | null> {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const BROWSER_DECODABLE = new Set(['jpg', 'jpeg', 'jfif', 'png', 'gif', 'webp', 'avif', 'svg']);
    if (!BROWSER_DECODABLE.has(ext)) return Promise.resolve(null);
    return new Promise(resolve => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.onerror = () => resolve(null);
      r.readAsDataURL(file);
    });
  }

  const handleFiles = useCallback((fileList) => {
    const arr = Array.from(fileList).filter(
      f => f.type.startsWith('image/') || SUPPORTED_EXT.test(f.name)
    );
    if (arr.length === 0) return;
    const newStatuses: Record<number, any> = {};
    const m = arr.map((f, idx) => {
      const { product, variant, position } = matchImageToProduct(f.name, products);
      const validationErr = validateFile(f);
      if (validationErr) {
        const globalIdx = matches.length + idx;
        newStatuses[globalIdx] = { status: 'invalid', reason: validationErr };
      }
      return { file: f, product, variant, position, thumbUrl: null };
    });
    setMatches(prev => {
      const startIdx = prev.length;
      // Generate thumbnails asynchronously — update in place once ready
      m.forEach((entry, i) => {
        readThumb(entry.file).then(url => {
          if (url) setMatches(p => p.map((x, j) => j === startIdx + i ? { ...x, thumbUrl: url } : x));
        });
      });
      return [...prev, ...m];
    });
    setFileStatuses(prev => ({ ...prev, ...newStatuses }));
    setManualAssignments({});
    setSearchQueries({});
    setOpenAssignIdx(null);
  }, [products, matches.length]);

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  }

  function getEffectiveProduct(m, i) {
    if (m.product) return m.product;
    const id = manualAssignments[i];
    if (id) return products.find(p => p.id === id) || null;
    return null;
  }

  const totalCount     = matches.length;
  const invalidCount   = matches.filter((_, i) => fileStatuses[i]?.status === 'invalid').length;
  const validCount     = totalCount - invalidCount;
  const matchedCount   = matches.filter((m, i) => fileStatuses[i]?.status !== 'invalid' && !!getEffectiveProduct(m, i)).length;
  const unmatchedCount = validCount - matchedCount;

  async function handleUpload() {
    // Skip files that are invalid or unmatched
    const uploadable = matches.map((m, i) => ({ m, i })).filter(({ m, i }) => {
      if (fileStatuses[i]?.status === 'invalid') return false;
      return !!getEffectiveProduct(m, i);
    });
    const total = uploadable.length;
    setUploadProgress({ done: 0, total });
    // Mark uploading files, preserve invalid statuses
    const initStatuses: Record<number, any> = {};
    uploadable.forEach(({ i }) => { initStatuses[i] = { status: 'uploading' }; });
    setFileStatuses(prev => ({ ...prev, ...initStatuses }));
    setSuccessCount(0);
    setMode('uploading');

    const CONCURRENCY = 3;
    let doneCount = 0; let successCnt = 0;

    async function runOne({ m, i }) {
      const product = getEffectiveProduct(m, i);
      if (!product) { setFileStatuses(s => ({ ...s, [i]: { status: 'skipped' } })); return; }
      const variantId = m.variant?.id || null;
      try {
        const data = await uploadFile(m.file, product.id, variantId);
        if (data.image) {
          successCnt++;
          onImagesUpdated?.(product.id, data.image);
          setFileStatuses(s => ({ ...s, [i]: { status: 'success' } }));
        } else {
          setFileStatuses(s => ({ ...s, [i]: { status: 'skipped' } }));
        }
      } catch (err) {
        setFileStatuses(s => ({ ...s, [i]: { status: 'error', reason: friendlyError(err.message) } }));
      }
      doneCount++;
      setUploadProgress(p => ({ ...p, done: doneCount }));
    }

    async function* chunks() {
      for (let i = 0; i < uploadable.length; i += CONCURRENCY) yield uploadable.slice(i, i + CONCURRENCY);
    }
    for await (const batch of chunks()) await Promise.all(batch.map(runOne));
    setSuccessCount(successCnt);
    setMode('complete');
  }

  /** Retry a single failed upload */
  async function handleRetry(idx: number) {
    const m = matches[idx];
    const product = getEffectiveProduct(m, idx);
    if (!product) return;
    setFileStatuses(s => ({ ...s, [idx]: { status: 'uploading' } }));
    const variantId = m.variant?.id || null;
    try {
      const data = await uploadFile(m.file, product.id, variantId);
      if (data.image) {
        setSuccessCount(c => c + 1);
        onImagesUpdated?.(product.id, data.image);
        setFileStatuses(s => ({ ...s, [idx]: { status: 'success' } }));
      } else {
        setFileStatuses(s => ({ ...s, [idx]: { status: 'skipped' } }));
      }
    } catch (err) {
      setFileStatuses(s => ({ ...s, [idx]: { status: 'error', reason: friendlyError(err.message) } }));
    }
  }

  const isUploading = mode === 'uploading';
  const isComplete  = mode === 'complete';

  /* auto-close 2 s after upload completes — only if ALL succeeded */
  const hasFailures = isComplete && Object.values(fileStatuses).some(
    s => s.status === 'error' || s.status === 'invalid'
  );
  useEffect(() => {
    if (!isComplete || hasFailures) return;
    const t = setTimeout(onClose, 2000);
    return () => clearTimeout(t);
  }, [isComplete, hasFailures, onClose]);

  /* footer meta — JSX with colored counts */
  let footerMeta;
  if (isUploading) {
    footerMeta = `Uploading ${uploadProgress.done} of ${uploadProgress.total}…`;
  } else if (totalCount === 0) {
    footerMeta = 'No files selected';
  } else {
    footerMeta = (
      <>
        {totalCount} file{totalCount !== 1 ? 's' : ''}{' · '}
        <span style={{ color: 'var(--color-green-text)' }}>{matchedCount} matched</span>
        {' · '}
        <span style={{ color: 'var(--color-yellow-text)' }}>{unmatchedCount} unmatched</span>
        {invalidCount > 0 && (
          <>{' · '}<span style={{ color: 'var(--color-red, #dc2626)' }}>{invalidCount} invalid</span></>
        )}
      </>
    );
  }

  return (
    <div
      className="iim-overlay"
      onClick={e => { if (e.target === e.currentTarget && !isUploading) onClose(); }}
    >
      <div className="iim-modal">

        {/* Header */}
        <div className="iim-modal-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="iim-modal-title">Import Images</div>
            {!isComplete && (
              <div className="iim-modal-subtitle">Upload product images. Matched automatically by SKU or product name.</div>
            )}
          </div>
          <button
            className="iim-close-btn"
            onClick={() => { if (!isUploading) onClose(); }}
            aria-label="Close"
          ><XIcon size={16} /></button>
        </div>

        {/* Content */}
        <div className="iim-tab-content">

          {isComplete ? (
            <CompleteView
              successCount={successCount}
              matches={matches}
              fileStatuses={fileStatuses}
              onRetry={handleRetry}
            />
          ) : isUploading ? (
            <div>
              <div style={{ fontSize: 13, color: 'var(--color-text-2)', fontWeight: 500 }}>
                Uploading {uploadProgress.done} of {uploadProgress.total} images…
              </div>
              <div className="iim-progress-bar-track">
                <div
                  className="iim-progress-bar-fill"
                  style={{ width: uploadProgress.total > 0 ? `${(uploadProgress.done / uploadProgress.total) * 100}%` : '0%' }}
                />
              </div>
            </div>
          ) : (
            <>
              {/* Drop zone */}
              <div
                className={`iim-dropzone${dragOver ? ' drag-over' : ''}`}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="iim-upload-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                </div>
                <div className="iim-dz-title">Drop images here</div>
                <div className="iim-dz-sub">
                  or{' '}
                  <button
                    className="iim-dz-link"
                    onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}
                  >browse files</button>
                  {' '}·{' '}or{' '}
                  <button
                    className="iim-dz-link"
                    onClick={e => { e.stopPropagation(); folderInputRef.current?.click(); }}
                  >select a folder</button>
                  {' '}— JPG, PNG, WebP, and more
                </div>
                <input
                  ref={fileInputRef}
                  type="file" multiple accept="image/*,.jpg,.jpeg,.png,.webp,.avif,.gif,.heic,.heif,.tiff,.tif,.bmp,.svg"
                  style={{ display: 'none' }}
                  onChange={e => handleFiles(e.target.files)}
                />
                <input
                  ref={folderInputRef}
                  type="file" multiple accept="image/*,.jpg,.jpeg,.png,.webp,.avif,.gif,.heic,.heif,.tiff,.tif,.bmp,.svg"
                  style={{ display: 'none' }}
                  onChange={e => handleFiles(e.target.files)}
                  {...{ webkitdirectory: '', directory: '' }}
                />
              </div>

              {/* SKU tip */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, padding: '8px 12px', background: 'var(--color-primary-light)', borderRadius: 'var(--r-sm)', fontSize: 12, fontWeight: 400, color: 'var(--color-text-2)', border: 'none', outline: 'none' }}>
                <Info size={16} strokeWidth={1.5} color="var(--color-primary)" style={{ flexShrink: 0 }} />
                Name files using the product SKU for automatic matching (e.g. RC2.jpg). For multiple images per product, add a position number: RC2_1.jpg, RC2_2.jpg, RC2_3.jpg.
              </div>

              {/* Files list */}
              {matches.length > 0 && (
                <div className="iim-files-list">
                  {matches.map((m, i) => {
                    const effective  = getEffectiveProduct(m, i);
                    const isMatched  = !!effective;
                    const isAssignOpen = openAssignIdx === i;
                    const sq = searchQueries[i] || '';
                    const filtered = sq
                      ? products.filter(p => (p.title || '').toLowerCase().includes(sq.toLowerCase()))
                      : products;
                    const fStatus = fileStatuses[i];
                    const isInvalid = fStatus?.status === 'invalid';

                    return (
                      <div
                        key={i}
                        ref={el => { if (!isMatched) assignRefs.current[i] = el; }}
                        style={{
                          borderBottom: i < matches.length - 1 ? '1px solid var(--color-border)' : 'none',
                          opacity: isInvalid ? 0.6 : 1,
                        }}
                      >
                        <div className="iim-file-row-inner">
                          {/* Thumbnail preview */}
                          {m.thumbUrl ? (
                            <img src={m.thumbUrl} alt="" className="iim-file-thumb"
                              onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling && (e.currentTarget.nextElementSibling.style.display = 'flex'); }}
                            />
                          ) : null}
                          <div className="iim-file-thumb" style={{ display: m.thumbUrl ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-3)" strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
                          </div>
                          {/* Status icon */}
                          {fStatus && (
                            <div className="iim-status-icon">
                              {fStatus.status === 'uploading' && (
                                <svg className="iim-spinner" viewBox="0 0 24 24" fill="none">
                                  <circle cx="12" cy="12" r="10" stroke="var(--color-primary)" strokeWidth="3" strokeOpacity="0.25" />
                                  <path d="M12 2a10 10 0 0 1 10 10" stroke="var(--color-primary)" strokeWidth="3" strokeLinecap="round" />
                                </svg>
                              )}
                              {fStatus.status === 'success' && (
                                <svg viewBox="0 0 24 24" fill="none" stroke="var(--color-green-text, #15803D)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12"/>
                                </svg>
                              )}
                              {(fStatus.status === 'error' || fStatus.status === 'invalid') && (
                                <svg viewBox="0 0 24 24" fill="none" stroke="var(--color-red, #dc2626)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                              )}
                            </div>
                          )}
                          <span className="iim-file-name">{m.file.name.replace(/\.[^.]+$/, '')}</span>
                          {isInvalid ? (
                            <span className="iim-match-badge" style={{ background: 'var(--color-red-bg)', color: 'var(--color-red-text, #dc2626)' }}>
                              {fStatus.reason}
                            </span>
                          ) : isMatched ? (
                            <span className="iim-match-badge iim-match-green">
                              {m.position != null ? `Matched · Position ${m.position}` : 'Matched'} · {effective.title}
                            </span>
                          ) : (
                            <button
                              className="iim-match-badge iim-match-yellow"
                              onClick={() => setOpenAssignIdx(isAssignOpen ? null : i)}
                            >
                              Unmatched — assign
                            </button>
                          )}
                        </div>

                        {/* Inline error message for failed uploads */}
                        {fStatus?.status === 'error' && fStatus.reason && (
                          <div className="iim-file-error">{fStatus.reason}</div>
                        )}

                        {!isMatched && !isInvalid && isAssignOpen && (
                          <div className="iim-assign-panel">
                            <input
                              autoFocus
                              className="iim-assign-input"
                              type="text"
                              placeholder="Search product…"
                              value={sq}
                              onChange={e => setSearchQueries(q => ({ ...q, [i]: e.target.value }))}
                            />
                            <div className="iim-assign-list">
                              {filtered.length === 0 ? (
                                <div style={{ padding: '8px 10px', fontSize: 12, color: 'var(--color-text-3)' }}>
                                  No products found
                                </div>
                              ) : filtered.slice(0, 10).map(p => (
                                <div
                                  key={p.id}
                                  className="iim-assign-item"
                                  onMouseDown={() => {
                                    setManualAssignments(a => ({ ...a, [i]: p.id }));
                                    setSearchQueries(q => ({ ...q, [i]: '' }));
                                    setOpenAssignIdx(null);
                                  }}
                                >
                                  {p.title || 'Untitled'}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="iim-modal-footer">
          {isComplete ? (
            <div className="iim-footer-actions" style={{ marginLeft: 'auto' }}>
              <button className="iim-btn-primary" onClick={onClose}>Done</button>
            </div>
          ) : (
            <>
              <span className="iim-footer-meta">{footerMeta}</span>
              {!isUploading && (
                <div className="iim-footer-actions">
                  <button className="iim-btn-ghost" onClick={onClose}>Cancel</button>
                  <button
                    className="iim-btn-primary"
                    onClick={handleUpload}
                    disabled={totalCount === 0}
                  >
                    {totalCount === 0 ? 'Import Images' : `Import ${totalCount} image${totalCount !== 1 ? 's' : ''}`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

      </div>
    </div>
  );
}

function CompleteView({ successCount, matches, fileStatuses, onRetry }) {
  const failed = matches.map((m, i) => ({ m, i })).filter(({ i }) => {
    const s = fileStatuses[i]?.status;
    return s === 'error' || s === 'invalid';
  });
  const retrying = Object.values(fileStatuses).some(s => s.status === 'uploading');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '16px 0' }}>
        {failed.length === 0 ? (
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--color-green-text, #15803D)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        ) : (
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--color-yellow-text, #a16207)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        )}
        <p style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--color-text)', textAlign: 'center' }}>
          {successCount} image{successCount !== 1 ? 's' : ''} added
          {failed.length > 0 ? ` · ${failed.length} failed` : ' successfully'}
        </p>
      </div>
      {failed.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-2)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Failed uploads
          </div>
          {failed.map(({ m, i }) => {
            const fStatus = fileStatuses[i];
            const isRetrying = fStatus?.status === 'uploading';
            const canRetry = fStatus?.status === 'error' && onRetry;
            return (
              <div key={i} style={{ padding: '8px 12px', background: 'var(--color-red-bg)', border: '1px solid var(--color-red-border, var(--color-border))', borderRadius: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {m.thumbUrl && <img src={m.thumbUrl} alt="" style={{ width: 24, height: 24, borderRadius: 3, objectFit: 'cover', flexShrink: 0 }} />}
                  <span style={{ flex: 1, fontSize: 12, color: 'var(--color-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.file.name.replace(/\.[^.]+$/, '')}
                  </span>
                  {canRetry && (
                    <button className="iim-retry-btn" onClick={() => onRetry(i)}>Retry</button>
                  )}
                  {isRetrying && (
                    <svg className="iim-spinner" viewBox="0 0 24 24" fill="none" style={{ width: 14, height: 14 }}>
                      <circle cx="12" cy="12" r="10" stroke="var(--color-primary)" strokeWidth="3" strokeOpacity="0.25" />
                      <path d="M12 2a10 10 0 0 1 10 10" stroke="var(--color-primary)" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                  )}
                </div>
                {fStatus?.reason && (
                  <div style={{ fontSize: 11, color: 'var(--color-red-text, #dc2626)', marginTop: 4, lineHeight: 1.3 }}>
                    {fStatus.reason}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
